import { useSignal } from "@preact/signals";
import { type BillingInfo, PACKS, type Pack, packCredits } from "~/protocol/billing";
import { setTopUp } from "~/web/src/api";
import { errorText, OFFLINE } from "~/web/src/errors";
import { creditsCount } from "~/web/src/pages/topup-copy";

// Automatic top-up on /billing (billing-spec decision 16): "when credits fall below X, buy the $N
// pack". Every member reads whether it is on; an org admin of a subscribed org sets it. A refused
// charge turns it off, and the page tells admins so until one saves it again.

/** The admin's form: on or off, the pack, and the number credits must fall below. */
export function TopUpForm({ b, onSaved }: { b: BillingInfo; onSaved: () => Promise<void> }) {
  const on = useSignal(b.topUp !== null);
  const pack = useSignal<Pack>(b.topUp?.pack ?? 10);
  const threshold = useSignal(String(b.topUp?.threshold ?? 5));
  const busy = useSignal(false);
  const said = useSignal<{ error: boolean; text: string } | null>(null);
  const most = packCredits(pack.value);
  const n = Number(threshold.value);
  const valid = Number.isInteger(n) && n >= 1 && n <= most;

  async function save(e: Event) {
    e.preventDefault();
    if (busy.value) return;
    if (on.value && !valid) {
      said.value = { error: true, text: `Enter a whole number from 1 to ${most}.` };
      return;
    }
    busy.value = true;
    said.value = null;
    try {
      await setTopUp(on.value ? { pack: pack.value, threshold: n } : null);
    } catch (err) {
      // The server's refusals are sentences; anything else is a bare status.
      const reason = errorText(err);
      said.value = {
        error: true,
        text:
          err instanceof TypeError
            ? OFFLINE
            : /^\d{3} /.test(reason)
              ? "Couldn't save top-up. Try again in a minute."
              : reason,
      };
      busy.value = false;
      return;
    }
    // Saved, whatever the refresh does: a failed one only leaves the page behind.
    said.value = { error: false, text: "Saved." };
    await onSaved().catch(() => {
      said.value = { error: false, text: "Saved. Reload this page to see the change." };
    });
    busy.value = false;
  }

  return (
    <form class="topup" noValidate onSubmit={(e) => void save(e)}>
      <h2>Automatic top-up</h2>
      <p class="dim">
        Top-up buys a credit pack with your org's saved card each time credits fall below the number
        you set. There's no limit on how often. If a charge is refused, top-up turns off. Pack
        prices are plus tax.
      </p>
      <label class="check">
        <input
          type="checkbox"
          checked={on.value}
          onChange={(e) => {
            on.value = e.currentTarget.checked;
            said.value = null;
          }}
        />
        Top up automatically
      </label>
      {on.value && (
        <>
          <fieldset>
            <legend>Pack to buy</legend>
            <div class="pack-choices">
              {PACKS.map((p) => (
                <label key={p} class={`pack-choice ${pack.value === p ? "on" : ""}`}>
                  <input
                    type="radio"
                    name="topup-pack"
                    class="sr"
                    checked={pack.value === p}
                    onChange={() => {
                      pack.value = p;
                      said.value = null;
                    }}
                  />
                  <strong>${p}</strong>
                  <span>{creditsCount(packCredits(p))}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <label class="threshold">
            <span>When credits fall below</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={most}
              step={1}
              value={threshold.value}
              // Marked only beside its message: typing clears both.
              aria-invalid={!valid && said.value?.error === true}
              aria-describedby="topup-hint topup-said"
              onInput={(e) => {
                threshold.value = e.currentTarget.value;
                said.value = null;
              }}
            />
          </label>
          <p id="topup-hint" class="dim">
            From 1 to {most}, so one ${pack.value} pack always brings your credits back up to at
            least the number you set.
          </p>
        </>
      )}
      <div class="topup-save">
        <button type="submit" class="btn" aria-disabled={busy.value}>
          {busy.value ? "Saving…" : "Save top-up"}
        </button>
        <span
          id="topup-said"
          class={said.value?.error ? "error" : "dim"}
          role="status"
          aria-live="polite"
        >
          {said.value?.text}
        </span>
      </div>
    </form>
  );
}
